import { jsPDF } from 'jspdf';

export interface ExportRow {
  name: string;
  value: number;
  label: string;
}

export interface ExportBoard {
  title: string;
  subtitle: string;
  accent: [number, number, number];
  rows: ExportRow[];
}

// One line of the overall points account ("Gesamtkonto"). Points come from
// placing top three in each category (gold 3, silver 2, bronze 1). The person
// with the most points may wish for an ice cream flavour.
export interface ExportStanding {
  name: string;
  points: number;
  gold: number;
  silver: number;
  bronze: number;
  // Competition rank (1,2,2,4…): employees with equal points share a place.
  place: number;
}

// Bonus per rank (1/2/3) shown as a badge on the top three of every board.
const BONUS = [30, 20, 10];

const C = {
  navy: [30, 41, 59] as const,
  slate: [100, 116, 139] as const,
  grayLine: [226, 232, 240] as const,
  gold: [245, 158, 11] as const,
  silver: [148, 163, 184] as const,
  bronze: [180, 83, 9] as const,
  barBg: [241, 245, 249] as const,
  white: [255, 255, 255] as const,
  bonus: [22, 163, 74] as const,
  pink: [231, 0, 125] as const,
  pinkSoft: [252, 231, 243] as const,
};

function drawBoard(doc: jsPDF, b: ExportBoard, x: number, y: number, w: number, h: number): void {
  doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text(b.title, x + 2, y + 6);
  doc.setTextColor(...C.slate); doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  doc.text(b.subtitle, x + 2, y + 10.5);

  const rows = b.rows.slice(0, 5);
  const maxVal = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  const rowsTop = y + 15;
  const rowH = (h - 17) / 5;

  rows.forEach((r, i) => {
    const ry = rowsTop + i * rowH;
    const medal = i < 3 ? [C.gold, C.silver, C.bronze][i] : C.silver;
    doc.setFillColor(medal[0], medal[1], medal[2]); doc.circle(x + 4.2, ry + 2.6, 2.4, 'F');
    doc.setTextColor(...C.white); doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    doc.text(String(i + 1), x + 4.2, ry + 2.7, { align: 'center', baseline: 'middle' });

    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
    doc.text(r.name, x + 8.5, ry + 3.4);

    let valRight = x + w - 2;
    if (i < 3) {
      const bw = 11, bx = x + w - bw - 1, by = ry + 0.3;
      doc.setFillColor(232, 245, 236); doc.roundedRect(bx, by, bw, 4.6, 1.2, 1.2, 'F');
      doc.setTextColor(...C.bonus); doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5);
      doc.text('€' + BONUS[i], bx + bw / 2, by + 2.5, { align: 'center', baseline: 'middle' });
      valRight = bx - 2;
    }
    doc.setTextColor(...C.slate); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(r.label, valRight, ry + 3.4, { align: 'right' });

    const barX = x + 8.5, barW = x + w - 2 - barX, barY = ry + 5.4;
    doc.setFillColor(...C.barBg); doc.roundedRect(barX, barY, barW, 2.1, 1, 1, 'F');
    const fillW = Math.max(1.5, (r.value / maxVal) * barW);
    doc.setFillColor(...b.accent); doc.roundedRect(barX, barY, fillW, 2.1, 1, 1, 'F');
  });

  doc.setDrawColor(...C.grayLine); doc.setLineWidth(0.2); doc.roundedRect(x, y, w, h, 2, 2, 'S');
}

// Draws a small count badge (a filled dot with a number) in a medal colour —
// used to show how many golds/silvers/bronzes an employee collected.
function drawMedalCount(doc: jsPDF, cx: number, cy: number, count: number, color: readonly [number, number, number]): void {
  doc.setFillColor(color[0], color[1], color[2]); doc.circle(cx, cy, 2, 'F');
  doc.setTextColor(...C.white); doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5);
  doc.text(String(count), cx, cy + 0.1, { align: 'center', baseline: 'middle' });
}

/**
 * Draws the "Gesamtkonto" panel: the overall points ranking across all
 * categories. Rank one is highlighted as the ice-cream winner.
 */
function drawStandings(doc: jsPDF, standings: ExportStanding[], x: number, y: number, w: number, h: number): void {
  doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('Gesamtkonto', x + 3, y + 7);
  doc.setTextColor(...C.slate); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  doc.text('Punkte gesamt (Gold 3 · Silber 2 · Bronze 1) — Team Member mit meisten Punkten darf sich eine Eissorte wünschen', x + 3, y + 11.5);

  // Show the top five places. Because equal points share a place, a tie can
  // list more than five people (all sharing that place still appear).
  const rows = standings.filter((s) => s.place <= 5);
  const maxPts = rows.reduce((m, r) => Math.max(m, r.points), 0) || 1;
  const rowsTop = y + 15.5;
  const rowH = rows.length > 0 ? (h - 17.5) / rows.length : 0;

  rows.forEach((s, i) => {
    const ry = rowsTop + i * rowH;
    const rowCy = ry + rowH / 2 - 0.5;
    const winner = s.place === 1;

    if (winner) {
      doc.setFillColor(...C.pinkSoft);
      doc.roundedRect(x + 1.5, ry + 0.5, w - 3, rowH - 1.5, 2, 2, 'F');
    }

    const medal = s.place <= 3 ? [C.gold, C.silver, C.bronze][s.place - 1] : C.slate;
    doc.setFillColor(medal[0], medal[1], medal[2]); doc.circle(x + 6, rowCy, 3, 'F');
    doc.setTextColor(...C.white); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text(String(s.place), x + 6, rowCy + 0.1, { align: 'center', baseline: 'middle' });

    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text(s.name, x + 11.5, rowCy + 0.2, { baseline: 'middle' });

    const nameW = doc.getTextWidth(s.name);
    if (winner) {
      const pillX = x + 11.5 + nameW + 3, pillW = 20;
      doc.setFillColor(...C.pink); doc.roundedRect(pillX, rowCy - 2.4, pillW, 4.8, 2.4, 2.4, 'F');
      doc.setTextColor(...C.white); doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5);
      doc.text('EISSORTE', pillX + pillW / 2, rowCy + 0.1, { align: 'center', baseline: 'middle' });
    }

    const ptsRight = x + w - 4;
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text(`${s.points} Pkt`, ptsRight, rowCy + 0.2, { baseline: 'middle', align: 'right' });

    let dotCx = x + w - 30;
    ([[s.gold, C.gold], [s.silver, C.silver], [s.bronze, C.bronze]] as const).forEach(([count, color]) => {
      if (count > 0) { drawMedalCount(doc, dotCx, rowCy, count, color); dotCx += 6; }
    });

    const barX = x + 11.5, barMaxW = x + w - 34 - barX, barY = rowCy + 2.6;
    if (barMaxW > 4) {
      doc.setFillColor(...C.barBg); doc.roundedRect(barX, barY, barMaxW, 1.8, 0.9, 0.9, 'F');
      const fillW = Math.max(1.5, (s.points / maxPts) * barMaxW);
      const barColor = winner ? C.pink : C.slate;
      doc.setFillColor(barColor[0], barColor[1], barColor[2]); doc.roundedRect(barX, barY, fillW, 1.8, 0.9, 0.9, 'F');
    }
  });

  const border = rows.length > 0 ? C.pink : C.grayLine;
  doc.setDrawColor(border[0], border[1], border[2]);
  doc.setLineWidth(rows.length > 0 ? 0.4 : 0.2);
  doc.roundedRect(x, y, w, h, 2, 2, 'S');
}

/**
 * Build a Top-5 PDF: a 2×3 grid of category boards, plus a full-width
 * "Gesamtkonto" points ranking at the bottom, and trigger a download.
 */
export function exportDashboardPdf(
  boards: ExportBoard[],
  periodLabel: string,
  standings: ExportStanding[] = [],
  fileName?: string,
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, margin = 12, gutterX = 8, gutterY = 6;
  const colW = (W - 2 * margin - gutterX) / 2;

  doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text('Team Dashboard', margin, margin + 6);
  doc.setTextColor(...C.slate); doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text('Top 5 · ' + periodLabel, margin, margin + 12);
  doc.setDrawColor(...C.grayLine); doc.line(margin, margin + 14, W - margin, margin + 14);

  const blockTop = margin + 16;
  const hasStandings = standings.length > 0;
  const standingsH = hasStandings ? 56 : 0;
  const standingsGap = hasStandings ? gutterY : 0;
  const boardsAreaH = H - blockTop - margin - 2 * gutterY - standingsGap - standingsH;
  const blockH = boardsAreaH / 3;

  boards.slice(0, 6).forEach((b, idx) => {
    const col = idx % 2, row = Math.floor(idx / 2);
    const x = margin + col * (colW + gutterX);
    const y = blockTop + row * (blockH + gutterY);
    drawBoard(doc, b, x, y, colW, blockH);
  });

  if (hasStandings) {
    const standingsY = blockTop + 3 * blockH + 2 * gutterY + standingsGap;
    drawStandings(doc, standings, margin, standingsY, W - 2 * margin, standingsH);
  }

  doc.save(fileName || `team-dashboard-${periodLabel.replace(/\s+/g, '-').toLowerCase()}.pdf`);
}
